import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApiToast } from '../../hooks/useApiToast';
import { useToast } from '../../contexts/ToastContext';
import '../../styles/token-watchlist.css';

const TokenWatchlist = () => {
  const { tokens } = useAuth();
  const { handleTokenOperation } = useApiToast();
  const { showConfirmation } = useToast();
  const [watchlist, setWatchlist] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTokenAddress, setNewTokenAddress] = useState('');
  const [newTokenNotes, setNewTokenNotes] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepump.urion.live/api';

  useEffect(() => {
    if (tokens?.access_token) {
      fetchWatchlist();
    }
  }, [tokens]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchWatchlist = async () => {
    try {
      setIsLoading(true);
      const data = await makeAuthenticatedRequest('/tokens/watchlist');
      setWatchlist(data.data.watchlist);
      setStats(data.data.stats);
      setError(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToken = async (e) => {
    e.preventDefault();

    if (!newTokenAddress.trim()) {
      setError('Token address is required');
      return;
    }

    try {
      setIsAdding(true);
      setError(null);

      await handleTokenOperation(
        () => makeAuthenticatedRequest('/tokens/watchlist', {
          method: 'POST',
          body: JSON.stringify({
            token_address: newTokenAddress.trim(),
            notes: newTokenNotes.trim() || null
          })
        }),
        {
          operationType: 'add',
          tokenSymbol: 'Token'
        }
      );

      // Refresh watchlist
      await fetchWatchlist();

      // Reset form
      setNewTokenAddress('');
      setNewTokenNotes('');
      setShowAddForm(false);

    } catch (error) {
      // Error is already handled by handleTokenOperation
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveToken = async (tokenAddress, tokenSymbol = 'Token') => {
    showConfirmation(
      `Are you sure you want to remove ${tokenSymbol} from your watchlist?`,
      async () => {
        try {
          await handleTokenOperation(
            () => makeAuthenticatedRequest(`/tokens/watchlist/${tokenAddress}`, {
              method: 'DELETE'
            }),
            {
              operationType: 'remove',
              tokenSymbol: tokenSymbol
            }
          );

          // Refresh watchlist
          await fetchWatchlist();
        } catch (error) {
          // Error is already handled by handleTokenOperation
        }
      },
      () => {
        // User cancelled - no action needed
      }
    );
  };

  const formatPrice = (price) => {
    if (price === 0) return '$0.00';
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatNumber = (num) => {
    if (num === 0) return '0';
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toFixed(2);
  };

  const formatPercentage = (percent) => {
    const formatted = percent.toFixed(2);
    return `${percent >= 0 ? '+' : ''}${formatted}%`;
  };

  if (isLoading) {
    return (
      <div className="token-watchlist">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your watchlist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="token-watchlist">
      <div className="watchlist-header">
        <div className="header-info">
          <h2>Token Watchlist</h2>
          <p>Track your favorite tokens and monitor their performance</p>
        </div>
        <button
          className="add-token-button"
          onClick={() => setShowAddForm(true)}
        >
          + Add Token
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          {error}
          <button className="error-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Watchlist Stats */}
      {watchlist.length > 0 && (
        <div className="watchlist-stats">
          <div className="stat-item">
            <span className="stat-label">Total Tokens</span>
            <span className="stat-value">{stats.total_tokens || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Gainers</span>
            <span className="stat-value positive">{stats.gainers || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Losers</span>
            <span className="stat-value negative">{stats.losers || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Avg Change</span>
            <span className={`stat-value ${stats.avg_change_24h >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(stats.avg_change_24h || 0)}
            </span>
          </div>
        </div>
      )}

      {/* Add Token Form */}
      {showAddForm && (
        <div className="add-token-form" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="form-header">
            <h3>Add Token to Watchlist...</h3>
            <button
              className="close-button"
              onClick={() => setShowAddForm(false)}
            >
              ×
            </button>
          </div>
          <form onSubmit={handleAddToken}>
            <div className="form-group">
              <label>Token Mint Address</label>
              <input
                type="text"
                value={newTokenAddress}
                onChange={(e) => setNewTokenAddress(e.target.value)}
                placeholder="Enter Solana token mint address (e.g., EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v)"
                disabled={isAdding}
                required
              />
              <div className="field-help">
                <p>Enter the token's mint address. You can find this on DexScreener, Solscan, or other Solana explorers.</p>
              </div>
            </div>
            <div className="form-group">
              <label>Notes (Optional)</label>
              <textarea
                value={newTokenNotes}
                onChange={(e) => setNewTokenNotes(e.target.value)}
                placeholder="Add personal notes about this token..."
                disabled={isAdding}
                rows="3"
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={() => setShowAddForm(false)}
                disabled={isAdding}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="add-button"
                disabled={isAdding || !newTokenAddress.trim()}
              >
                {isAdding ? 'Adding...' : 'Add Token'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Watchlist Table */}
      {watchlist.length === 0 ? (
        <div className="empty-watchlist">
          <div className="empty-icon">📊</div>
          <h3>No Tokens in Watchlist</h3>
          <p>Add tokens by their mint address to start tracking their performance</p>
          <button
            className="add-first-token-button"
            onClick={() => setShowAddForm(true)}
          >
            Add Your First Token
          </button>
        </div>
      ) : (
        <div className="watchlist-table">
          <div className="table-header">
            <div className="col-token">Token</div>
            <div className="col-price">Price</div>
            <div className="col-change">24h Change</div>
            <div className="col-volume">Volume</div>
            <div className="col-market-cap">Market Cap</div>
            <div className="col-actions">Actions</div>
          </div>

          {watchlist.map((token) => (
            <div key={token.token_address} className="table-row">
              <div className="col-token">
                <div className="token-info">
                  {token.current_data.image_url && (
                    <img
                      src={token.current_data.image_url}
                      alt={token.token_symbol}
                      className="token-image"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <div className="token-details">
                    <span className="token-symbol">{token.token_symbol}</span>
                    <span className="token-name">{token.token_name}</span>
                    {token.notes && (
                      <span className="token-notes">{token.notes}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-price">
                {formatPrice(token.current_data.price)}
              </div>

              <div className={`col-change ${token.current_data.price_change_24h >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(token.current_data.price_change_24h)}
              </div>

              <div className="col-volume">
                ${formatNumber(token.current_data.volume_24h)}
              </div>

              <div className="col-market-cap">
                ${formatNumber(token.current_data.market_cap)}
              </div>

              <div className="col-actions">
                <button
                  className="remove-button"
                  onClick={() => handleRemoveToken(token.token_address, token.token_symbol)}
                  title="Remove from watchlist"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TokenWatchlist;